import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, BarChart3, Film, Lightbulb, Sparkles, Calendar,
  CreditCard, Settings, Shield, LogOut, Menu, X,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NotificationBell } from "@/components/NotificationBell";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/i18n";

export const DashboardLayout = ({ children }) => {
  const { t } = useT();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const nav = [
    { to: "/dashboard", icon: LayoutDashboard, label: t("nav.dashboard") },
    { to: "/analytics", icon: BarChart3, label: t("nav.analytics") },
    { to: "/reels", icon: Film, label: t("nav.reels") },
    { to: "/insights", icon: Lightbulb, label: t("nav.insights") },
    { to: "/content-ai", icon: Sparkles, label: t("nav.contentAI") },
    { to: "/calendar", icon: Calendar, label: t("nav.calendar") },
    { to: "/billing", icon: CreditCard, label: t("nav.billing") },
    { to: "/account", icon: Settings, label: t("nav.account") },
  ];
  if (user?.is_admin) nav.push({ to: "/admin", icon: Shield, label: t("nav.admin") });

  const handleLogout = async () => { await logout(); navigate("/"); };

  const NavItems = () => (
    <nav className="space-y-1 px-3">
      {nav.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={() => setMobileOpen(false)}
          data-testid={`nav-${item.to.replace("/", "")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${
              isActive ? "bg-brand-500/10 text-brand-700 dark:text-brand-400" : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`
          }
        >
          <item.icon className="h-[18px] w-[18px]" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar desktop */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-border bg-card/60 backdrop-blur lg:flex">
        <div className="flex h-16 items-center px-5">
          <NavLink to="/dashboard"><Logo /></NavLink>
        </div>
        <div className="flex-1 overflow-y-auto py-4"><NavItems /></div>
        <div className="border-t border-border p-3">
          <button onClick={handleLogout} data-testid="logout-btn"
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors">
            <LogOut className="h-[18px] w-[18px]" /> {t("nav.logout")}
          </button>
        </div>
      </aside>

      {/* Mobile drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)} className="fixed inset-0 z-40 bg-black/50 lg:hidden" />
            <motion.aside initial={{ x: -300 }} animate={{ x: 0 }} exit={{ x: -300 }}
              transition={{ type: "spring", damping: 26, stiffness: 260 }}
              className="fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-border bg-card lg:hidden">
              <div className="flex h-16 items-center justify-between px-5">
                <Logo />
                <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)}><X className="h-5 w-5" /></Button>
              </div>
              <div className="flex-1 overflow-y-auto py-4"><NavItems /></div>
              <div className="border-t border-border p-3">
                <button onClick={handleLogout} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-muted-foreground hover:bg-muted">
                  <LogOut className="h-[18px] w-[18px]" /> {t("nav.logout")}
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-3 border-b border-border bg-background/80 px-4 backdrop-blur sm:px-6">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </Button>
          </div>
          <div className="flex items-center gap-1.5">
            <NotificationBell />
            <ThemeToggle />
            <Avatar className="h-9 w-9 ml-1" data-testid="user-avatar">
              {user?.picture && <AvatarImage src={user.picture} />}
              <AvatarFallback className="bg-brand-500/15 text-brand-700 dark:text-brand-400 text-sm font-semibold">
                {(user?.name || "U").slice(0, 1).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </div>
        </header>
        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">{children}</main>
      </div>
    </div>
  );
};
